import { useMe } from '../../../organisation/useMe';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { screeningSelectorsApi } from '../../../apis';
import {
  InternalApiEntityType,
  InternalApiScreeningFlow,
  InternalApiUpdateListSelectorGroupCommand,
  UpdateSelectorGroupByCodeRequest,
} from '../../../api-internal';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useQueryScreeningFlows } from '../new/screeningFlowActions';

export const useQueryScreeningSelectors = (listId: string | undefined) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    enabled: !!listId,
    queryKey: ['screeningSelectors', listId, currentOrganisation?.id],
    queryFn: () => screeningSelectorsApi.findSelectorOptionsForList({ listId: listId! }),
  });
};

export const useQueryScreeningSelectorsGroups = (listId: string | undefined) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    enabled: !!listId,
    queryKey: ['screeningSelectorsGroups', listId, currentOrganisation?.id],
    queryFn: () => screeningSelectorsApi.findSelectorGroupsForList({ listId: listId! }),
  });
};

export const useCreateScreeningSelectorGroup = (listId: string | undefined) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (internalApiUpdateListSelectorGroupCommand: InternalApiUpdateListSelectorGroupCommand) =>
      screeningSelectorsApi.createSelectorGroup({
        listId: listId!,
        internalApiUpdateListSelectorGroupCommand,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['screeningSelectorsGroups', listId, currentOrganisation?.id],
      });
    },
  });
};

export const useUpdateScreeningSelectorGroup = (listId: string | undefined) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  const dispatch = useDispatch();
  const { data: personScreeningFlows } = useQueryScreeningFlows(InternalApiEntityType.PERSON);
  const { data: transactionScreeningFlows } = useQueryScreeningFlows(InternalApiEntityType.TRANSACTION);

  return useMutation({
    mutationFn: async (req: Omit<UpdateSelectorGroupByCodeRequest, 'listId'>) => {
      const update = () =>
        screeningSelectorsApi.updateSelectorGroupByCode({
          listId: listId!,
          ...req,
        });

      if (isGroupInUse(req.groupCode, [...(personScreeningFlows ?? []), ...(transactionScreeningFlows ?? [])])) {
        const confirm = await dispatch(
          showConfirmationModal({
            title: 'This List Group is currently used in a Screening Flow',
          })
        );
        return await (confirm ? update() : new Promise<undefined>(() => undefined));
      } else {
        return update();
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['screeningSelectorsGroups', listId, currentOrganisation?.id],
      });
    },
  });
};

export const useDeleteScreeningSelectorGroup = (listId: string | undefined) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  const dispatch = useDispatch();
  const { data: personScreeningFlows } = useQueryScreeningFlows(InternalApiEntityType.PERSON);
  const { data: transactionScreeningFlows } = useQueryScreeningFlows(InternalApiEntityType.TRANSACTION);

  return useMutation({
    mutationFn: (groupCode: string) => {
      if (isGroupInUse(groupCode, [...(personScreeningFlows ?? []), ...(transactionScreeningFlows ?? [])])) {
        dispatch(
          showConfirmationModal({
            title: 'This List Group is currently used in a Screening Flow. It cannot be deleted.',
            onConfirm: () => {},
          })
        );
        return new Promise(() => null);
      } else {
        return screeningSelectorsApi.deleteSelectorGroupByCode({
          listId: listId!,
          groupCode,
        });
      }
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['screeningSelectorsGroups', listId, currentOrganisation?.id],
      });
    },
  });
};

const isGroupInUse = (groupCode: string, screeningFlows: InternalApiScreeningFlow[]) => {
  return screeningFlows
    ? screeningFlows.some((flow) => flow.lists?.some((list) => list.selectorGroupCodes?.includes(groupCode)))
    : false;
};
